import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Plus, X, Users, Gamepad2 } from 'lucide-react';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Input } from '../components/Input';
import { LanguageSelector } from '../components/LanguageSelector';

interface LocalGameSetupPageProps {
  onBack: () => void;
  onStartGame: (config: LocalGameConfig) => void;
  savedConfig?: LocalGameConfig | null;
}

export interface LocalGameConfig {
  players: string[];
  category: string;
  impostorCount: number;
}

const categories = [
  { id: 'animales', name: 'Animales' },
  { id: 'comida', name: 'Comida' },
  { id: 'objetos', name: 'Objetos' },
  { id: 'películas', name: 'Películas' },
  { id: 'lugares', name: 'Lugares' },
  { id: 'profesiones', name: 'Profesiones' },
];

export const LocalGameSetupPage: React.FC<LocalGameSetupPageProps> = ({
  onBack,
  onStartGame,
  savedConfig,
}) => {
  const { t } = useTranslation();
  const [playerNames, setPlayerNames] = useState(savedConfig?.players || ['', '', '']);
  const [selectedCategory, setSelectedCategory] = useState(savedConfig?.category || 'animales');
  const [impostorCount, setImpostorCount] = useState(savedConfig?.impostorCount || 1);

  // Actualizar estados si savedConfig cambia
  useEffect(() => {
    if (savedConfig) {
      setPlayerNames(savedConfig.players);
      setSelectedCategory(savedConfig.category);
      setImpostorCount(savedConfig.impostorCount);
    }
  }, [savedConfig]);

  const addPlayer = () => {
    setPlayerNames([...playerNames, '']);
  };

  const removePlayer = (index: number) => {
    if (playerNames.length > 3) {
      setPlayerNames(playerNames.filter((_, i) => i !== index));
    }
  };

  const updatePlayer = (index: number, name: string) => {
    const newPlayers = [...playerNames];
    newPlayers[index] = name;
    setPlayerNames(newPlayers);
  };

  const handleStartGame = () => {
    const validPlayers = playerNames.filter(name => name.trim());
    
    if (validPlayers.length < 3) {
      alert('Se necesitan al menos 3 jugadores');
      return;
    }

    // Validar cantidad de impostores
    if (validPlayers.length === 3 && impostorCount !== 1) {
      alert('Con 3 jugadores solo puede haber 1 impostor');
      return;
    }

    if (validPlayers.length >= 4 && validPlayers.length <= 6) {
      if (impostorCount < 1 || impostorCount > 2) {
        alert('Con 4-6 jugadores debe haber 1 o 2 impostores');
        return;
      }
    }

    onStartGame({
      players: validPlayers,
      category: selectedCategory,
      impostorCount,
    });
  };

  const validPlayerCount = playerNames.filter(name => name.trim()).length;
  const maxImpostors = validPlayerCount === 3 ? 1 : validPlayerCount >= 4 && validPlayerCount <= 6 ? 2 : 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%239C92AC%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20 pointer-events-none"></div>
      
      {/* Language Selector */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector />
      </div>

      {/* Back Button */}
      <div className="absolute top-4 left-4 z-10">
        <Button
          variant="secondary"
          size="sm"
          onClick={onBack}
          icon={<ArrowLeft className="w-4 h-4" />}
        >
          Atrás
        </Button>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-2xl animate-fade-in">
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 font-display">
            🎮 Modo Local
          </h1>
          <p className="text-white/70 text-lg">
            Configura tu partida local
          </p>
        </div>

        <Card className="mb-6">
          {/* Players Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Jugadores (mínimo 3)
            </h3>
            <div className="space-y-3">
              {playerNames.map((player, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    placeholder={`Nombre del jugador ${index + 1}`}
                    value={player}
                    onChange={(e) => updatePlayer(index, e.target.value)}
                    icon={<Users className="w-5 h-5" />}
                  />
                  {playerNames.length > 3 && (
                    <Button
                      variant="danger"
                      size="md"
                      onClick={() => removePlayer(index)}
                      icon={<X className="w-4 h-4" />}
                    >
                      Eliminar
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <Button
              variant="secondary"
              size="md"
              onClick={addPlayer}
              icon={<Plus className="w-4 h-4" />}
              className="mt-3"
            >
              Agregar Jugador
            </Button>
          </div>

          {/* Category Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-4">
              📚 Categoría de palabras
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`p-4 rounded-xl transition-all duration-200 ${
                    selectedCategory === cat.id
                      ? 'bg-primary-500/20 border-2 border-primary-400'
                      : 'bg-white/5 border-2 border-transparent hover:bg-white/10'
                  }`}
                >
                  <p className="text-white font-semibold">{cat.name}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Impostor Count Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-4">
              🎭 Cantidad de Impostores
            </h3>
            <div className="flex gap-3">
              {[1, 2].map((count) => (
                <button
                  key={count}
                  onClick={() => setImpostorCount(count)}
                  disabled={count > maxImpostors}
                  className={`flex-1 p-4 rounded-xl transition-all duration-200 ${
                    impostorCount === count
                      ? 'bg-accent-500/20 border-2 border-accent-400'
                      : count > maxImpostors
                      ? 'bg-white/5 border-2 border-transparent opacity-50 cursor-not-allowed'
                      : 'bg-white/5 border-2 border-transparent hover:bg-white/10'
                  }`}
                >
                  <p className="text-white font-bold text-2xl">{count}</p>
                  <p className="text-white/70 text-sm">
                    {count === 1 ? 'Impostor' : 'Impostores'}
                  </p>
                </button>
              ))}
            </div>
            {validPlayerCount === 3 && (
              <p className="text-warning-400 text-sm mt-2">
                ⚠️ Con 3 jugadores solo puede haber 1 impostor
              </p>
            )}
            {validPlayerCount >= 4 && validPlayerCount <= 6 && (
              <p className="text-primary-400 text-sm mt-2">
                ℹ️ Con {validPlayerCount} jugadores puedes elegir 1 o 2 impostores
              </p>
            )}
          </div>

          {/* Start Button */}
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleStartGame}
            disabled={validPlayerCount < 3}
            icon={<Gamepad2 className="w-5 h-5" />}
          >
            Iniciar Partida
          </Button>
        </Card>
      </div>
    </div>
  );
};
